import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import BaseStep from './BaseStep'
import { useImportContactContext } from './ImportContactContext'
import * as S from './styles'
import Select from 'components/FormUtils/Select'
import apiService from 'services/api'

const schema = z.object({
  categoryId: z.number().optional(),
  subcategoryId: z.number().optional(),
})

export type OpportunityTypeFormType = z.infer<typeof schema>

export function OpportunityTypeStep() {
  const api = apiService()
  const { data: categories } = api.useGetActiveCompanyCategoriesAndTypes()

  const {
    methods: { handleClose, prevStep, nextStep, setOpportunityType },
    state: { opportunityType },
  } = useImportContactContext()

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<OpportunityTypeFormType>({
    resolver: zodResolver(schema),
    defaultValues: {
      categoryId: opportunityType?.categoryId || undefined,
      subcategoryId: opportunityType?.subcategoryId || undefined,
    },
    mode: 'onSubmit',
  })

  const watchedCategoryId = watch('categoryId')
  const watchedSubcategoryId = watch('subcategoryId')

  // Category options
  const categoryOptions = useMemo(() => {
    if (!categories) return []
    return categories.map((category) => ({
      label: category.name,
      value: category.id,
    }))
  }, [categories])

  // Subcategory options based on selected category
  const subcategoryOptions = useMemo(() => {
    if (!categories || !watchedCategoryId) return []

    const selectedCategory = categories.find(
      (category) => category.id === watchedCategoryId
    )

    if (!selectedCategory) return []

    return selectedCategory.sub_categories.map((subcategory) => ({
      label: subcategory.name,
      value: subcategory.id,
    }))
  }, [categories, watchedCategoryId])

  // Clear subcategory when category changes
  useEffect(() => {
    setValue('subcategoryId', undefined)
  }, [watchedCategoryId, setValue])

  // Check if continue should be enabled (either no category selected or subcategory is selected)
  const canContinue =
    watchedCategoryId === undefined || watchedSubcategoryId !== undefined

  const onSubmit = handleSubmit((data) => {
    setOpportunityType(data)
    nextStep()
  })

  const onSkip = () => {
    setOpportunityType({ categoryId: undefined, subcategoryId: undefined })
    nextStep()
  }

  return (
    <BaseStep
      handleClose={handleClose}
      onBack={prevStep}
      onContinue={onSubmit}
      onSkip={onSkip}
      title="Upload New Contacts - Type"
      backButtonText="Back"
      continueButtonText="Continue"
      canContinue={canContinue}
    >
      <S.Content>
        <S.ContentTitle>
          If the contacts all belong to the same opportunity category or type,
          you can identify these now for a faster import experience. Otherwise,
          just skip this step.
        </S.ContentTitle>

        <form onSubmit={onSubmit} className="max-w-2xl mx-auto mt-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Category & Type
            </label>

            <div className="flex gap-4 justify-center">
              <div className="w-[300px]">
                <Select
                  control={control}
                  name="categoryId"
                  placeholder="Select Category"
                  options={categoryOptions}
                  className="mt-0"
                  isClearable
                />
              </div>

              <div className="w-[300px]">
                <Select
                  control={control}
                  name="subcategoryId"
                  placeholder="Select Type"
                  options={subcategoryOptions}
                  className="mt-0"
                  isClearable
                  disabled={!watchedCategoryId}
                />
              </div>
            </div>

            {/* Show validation errors only when form has been submitted */}
            {Object.keys(errors).length > 0 && (
              <div className="mt-2 text-sm text-red-600">
                Please select a valid category and type, or skip this step.
              </div>
            )}
          </div>
        </form>
      </S.Content>
    </BaseStep>
  )
}
